import {
  InterviewDisplayConfig,
  InterviewLayout,
  InterviewModality,
  InterviewStatus
} from '@/types/pulse';
import { Interview, InterviewVariant, VariantConfig } from '@/types/pulsedb';
import { layoutRequiredByModality, modalityPinnedByLayout } from './layouts';

/**
 * Lays a variant's copy of a config over the interview's.
 *
 * A key the variant does not carry is inherited. A key it carries as null is
 * the variant turning that setting off, which the resolved config states the
 * way an interview states it — by not carrying the key at all. Nulls therefore
 * never leave this function, and callers read the result exactly as they read
 * an interview's own config.
 *
 * The merge is one level deep: a nested object such as `reward_config.copy` is
 * replaced whole rather than merged field by field.
 */
export function mergeInterviewConfig<T extends object>(
  interviewConfig: T | null | undefined,
  variantConfig: VariantConfig<T> | null | undefined
): T | undefined {
  const merged: Record<string, unknown> = {
    ...(interviewConfig ?? {}),
    ...(variantConfig ?? {})
  };

  for (const key of Object.keys(merged)) {
    if (merged[key] === null) delete merged[key];
  }

  return Object.keys(merged).length ? (merged as T) : undefined;
}

/**
 * A variant config with one of its keys set, turned off, or handed back to the
 * interview.
 *
 * `undefined` inherits, because a variant inherits a key by not carrying it —
 * so inheriting removes the key rather than storing anything for it. `null`
 * turns the setting off. A config left with no keys becomes null rather than an
 * empty object, which is how an interview with nothing set stores one too.
 */
export function setVariantConfigKey<T extends object, K extends keyof T>(
  config: VariantConfig<T> | null | undefined,
  key: K,
  value: T[K] | null | undefined
): VariantConfig<T> | null {
  const next: VariantConfig<T> = { ...(config ?? {}) };

  if (value === undefined) delete next[key];
  else next[key] = value;

  return Object.keys(next).length ? next : null;
}

/**
 * The branding a workspace supplies, which an interview falls back to before
 * the theme's own defaults.
 *
 * Deliberately narrower than a display config: a workspace states how its
 * interviews look, never how they behave, so `theme`, `layout` and
 * `fallbackModality` have no counterpart here and cannot be set from an
 * organization row.
 */
export interface OrgBranding {
  primaryColor?: string;
  btnColor?: string;
  logo?: string;
  captionButtonText?: string;
  prominentLogo?: boolean;
  showCreatePulseButton?: boolean;
  showTitleOnBrief?: boolean;
}

/** Text a workspace can supply, where empty counts as unset. */
const ORG_BRANDING_TEXT = [
  'primaryColor',
  'btnColor',
  'logo',
  'captionButtonText'
] as const;

/** Flags a workspace can supply, where an explicit false is a value. */
const ORG_BRANDING_FLAGS = [
  'prominentLogo',
  'showCreatePulseButton',
  'showTitleOnBrief'
] as const;

/**
 * The display config an interview actually runs, across all three layers that
 * can state one: the variant first, then the interview, then the workspace.
 *
 * The first two are rows of the same shape and merge as any other config does.
 * The workspace is a different thing — a fallback for branding alone — so it
 * fills only the fields the other two left unset, and only the fields it is
 * allowed to state. Emptiness is judged the way the theme judges it: text is
 * unset when it is blank, a flag only when it is absent, so an interview can
 * still turn a workspace flag off by setting it to false.
 *
 * The result is an ordinary display config, so callers read it without knowing
 * a workspace was involved.
 */
export function resolveDisplayConfig(
  interviewConfig: InterviewDisplayConfig | null | undefined,
  variantConfig: VariantConfig<InterviewDisplayConfig> | null | undefined,
  orgBranding?: OrgBranding | null
): InterviewDisplayConfig | undefined {
  const merged = mergeInterviewConfig(interviewConfig, variantConfig);
  if (!orgBranding) return merged;

  const resolved: InterviewDisplayConfig = { ...merged };

  for (const key of ORG_BRANDING_TEXT) {
    const value = orgBranding[key];
    if (!resolved[key] && value) resolved[key] = value;
  }

  for (const key of ORG_BRANDING_FLAGS) {
    const value = orgBranding[key];
    if (resolved[key] == null && value != null) resolved[key] = value;
  }

  return Object.keys(resolved).length ? resolved : undefined;
}

/**
 * The layout a variant actually runs.
 *
 * Guided and the ALL modality are two halves of one setting: guided runs no
 * other modality, and ALL runs under no other layout. They are resolved to and
 * away from together, so a variant can never land on half the pair — ALL takes
 * the guided layout with it, and any other modality drops guided to Speaker.
 *
 * Phone renders no layout at all, so it keeps whatever it inherited.
 */
function resolveVariantLayout(
  displayConfig: InterviewDisplayConfig | undefined,
  modality: InterviewModality
): InterviewDisplayConfig | undefined {
  if (modality === InterviewModality.PHONE) return displayConfig;

  const required = layoutRequiredByModality(modality);
  if (required) return { ...displayConfig, layout: required };

  if (modalityPinnedByLayout(displayConfig?.layout) !== undefined) {
    return { ...displayConfig, layout: InterviewLayout.SPEAKER };
  }

  return displayConfig;
}

/**
 * The interview a variant actually runs: the parent's values with the variant's
 * own laid over them. The single place the variant waterfall is applied — every
 * surface that renders or runs a variant should read what this returns rather
 * than reaching for either row directly.
 *
 * Identity is deliberate. `id` stays the interview's, because that is the
 * foreign key conversations, signals and reports are written against. `slug`
 * becomes the variant's, because that is the link the respondent opened. The
 * caller keeps the variant itself for `conversations.interview_variant_id`.
 *
 * A variant has no lifecycle of its own beyond being switched off. It runs
 * wherever its interview stands — draft with a draft, archived with an archived
 * one — and turning it off closes only that variant's link, which reads as the
 * COMPLETE the Close action writes so the runtime and its copy need know
 * nothing about variants.
 *
 * Passing no variant returns the interview untouched, so a call site can hand
 * over whatever it has without branching.
 */
export function resolveInterviewVariant(
  interview: Interview,
  variant: InterviewVariant | null | undefined,
  orgBranding?: OrgBranding | null
): Interview {
  if (!variant && !orgBranding) return interview;

  const modality = variant?.modality ?? interview.modality;

  const displayConfig = resolveDisplayConfig(
    interview.display_config,
    variant?.display_config,
    orgBranding
  );

  // `Interview` intersects the row's `Json | null` config columns with optional
  // typed fields, which collapses to a non-nullable object type — so a config
  // the resolved interview genuinely does not have cannot be expressed without
  // this cast.
  return {
    ...interview,
    // The variant's own identity rather than overrides of the interview, and
    // never null on a variant.
    slug: variant?.slug ?? interview.slug,
    status:
      variant && !variant.active ? InterviewStatus.COMPLETE : interview.status,
    name: variant?.name ?? interview.name,
    title: variant?.title ?? interview.title,
    caption: variant?.caption ?? interview.caption,
    modality,
    complete_config: mergeInterviewConfig(
      interview.complete_config,
      variant?.complete_config
    ),
    // Only a variant can land on half the guided/ALL pair, by overriding one
    // of them; an interview states both itself and keeps whatever it states.
    display_config: variant
      ? resolveVariantLayout(displayConfig, modality)
      : displayConfig,
    reward_config: mergeInterviewConfig(
      interview.reward_config,
      variant?.reward_config
    )
  } as Interview;
}
